import { apiService } from "./api";

export type LenderPartner = "volt" | "smallcase" | "finsire";
export type LenderPlatform = "Volt Money" | "South Indian Bank" | "Suryoday Small Finance Bank" | "City Union Bank";

export interface LenderDetails {
  interestRate: string;
  foreclosureCharges: string;
  tenure: string;
  processingFee: string;
  logoEmoji: string;
}

export interface Lender {
  platform: LenderPlatform;
  displayName: string;
  partner: LenderPartner;
  logo: string;
  details: LenderDetails;
}

const LOGO_DIR = "/assets/images/loan-providers/";

// Same data and order as Android types/loan.ts LENDER_OPTIONS
export const LENDERS: Lender[] = [
  {
    platform: "South Indian Bank",
    displayName: "South Indian Bank",
    partner: "smallcase",
    logo: LOGO_DIR + "south_indian_bank_logo.png",
    details: { logoEmoji: "📈", interestRate: "9.99% p.a.", foreclosureCharges: "0%", tenure: "36 Months", processingFee: "₹ 1250+GST*" },
  },
  {
    platform: "Suryoday Small Finance Bank",
    displayName: "Suryoday Small Finance Bank",
    partner: "finsire",
    logo: LOGO_DIR + "suryoday_small_finance_bank_logo.png",
    details: { logoEmoji: "🎯", interestRate: "9.99% p.a.", foreclosureCharges: "0%", tenure: "12 Months", processingFee: "₹ 999+GST*" },
  },
  {
    platform: "City Union Bank",
    displayName: "City Union Bank",
    partner: "finsire",
    logo: LOGO_DIR + "city_union_bank_logo.png",
    details: { logoEmoji: "🏦", interestRate: "9.99% p.a.", foreclosureCharges: "0%", tenure: "12 Months", processingFee: "₹ 999+GST*" },
  },
  {
    platform: "Volt Money",
    displayName: "DSP Finance",
    partner: "volt",
    logo: LOGO_DIR + "DSP_finance_logo.png",
    details: { logoEmoji: "⚡", interestRate: "10.49% p.a.", foreclosureCharges: "0%", tenure: "72 Months", processingFee: "₹ 499+GST*" },
  },
];

interface LenderRateOverride extends Partial<Omit<LenderDetails, "logoEmoji">> {
  platform: LenderPlatform;
}

/** Backend rate overrides merged over the defaults; falls back to defaults on any error. */
export const getLendersWithRates = async (): Promise<Lender[]> => {
  try {
    const res = await apiService.get<{ rates?: LenderRateOverride[] }>("/lenders/rates/");
    const byPlatform = new Map((res.data.rates || []).map((r) => [r.platform, r]));
    return LENDERS.map((l) => {
      const { platform, ...o } = byPlatform.get(l.platform) || ({} as LenderRateOverride);
      const clean = Object.fromEntries(Object.entries(o).filter(([, v]) => v != null));
      return { ...l, details: { ...l.details, ...clean } };
    });
  } catch {
    return LENDERS;
  }
};

export interface CreateUserLoanResponse {
  success: boolean;
  partner: string;
  message?: string;
  has_credit_line_active?: boolean;
  apply_credit_link?: string;
  data?: { interactionToken?: string; lid?: string };
}

export const createUserLoan = async (body: {
  phone_number: string;
  full_name: string;
  platform: LenderPlatform;
  email?: string;
  pan: string;
  latitude: number;
  longitude: number;
}): Promise<CreateUserLoanResponse> => {
  // backend reads `pan` (smallcase) and the app sends `pan_number`; send both
  const res = await apiService.post("/varicash/create-user-loan/", {
    ...body,
    pan_number: body.pan,
  });
  return res.data;
};

// Loans JS SDK: https://gateway.smallcase.com/scdk/2.0.0/scdk.js exposes window.ScLoan
const SDK_URL = "https://gateway.smallcase.com/scdk/2.0.0/scdk.js";
let sdkPromise: Promise<any> | null = null;

const loadSdk = (): Promise<any> => {
  if (!sdkPromise) {
    sdkPromise = new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = SDK_URL;
      s.onload = () =>
        resolve(new (window as any).ScLoan({ gatewayName: import.meta.env.VITE_SMALLCASE_GATEWAY_NAME || "varicash" }));
      s.onerror = () => reject(new Error("Failed to load smallcase SDK"));
      document.body.appendChild(s);
    }).catch((e) => {
      sdkPromise = null; // don't cache failure
      throw e;
    });
  }
  return sdkPromise;
};

export interface ScLoanError {
  code?: number;
  message: string;
}

/** Resolves with loan application status; rejects with ScLoanError-like object. */
export const triggerSmallcaseApply = async (interactionToken: string): Promise<string | undefined> => {
  const sdk = await loadSdk();
  const res = await sdk.apply({ interactionToken });
  return res?.loanApplication?.status;
};

// Loans SDK error codes (common to all platforms): https://developers.gateway.smallcase.com
const SC_ERRORS: Record<number, string> = {
  1012: "Application saved. You can resume anytime.", // user_cancelled
  1031: "Please contact support.", // contact_support
  1032: "Please allow the required browser permissions and try again.", // missing_permissions (web)
  2000: "Something went wrong. Please try again.", // internal_error
  2001: "Something went wrong. Please try again.", // missing_url_params
  3001: "You already have an existing loan.", // existing_loan_found
  3004: "Service not ready. Please refresh and try again.", // init_sdk
  4000: "Invalid request. Please try again.", // invalid_request
  4001: "This lender is currently unavailable.", // partner_inactive
  4002: "Please sign in to continue.", // guest_not_allowed
  4003: "Session expired. Please try again.", // interaction_expired
  4004: "Not available right now.", // invalid_intent
  4005: "Something went wrong. Please try again.", // invalid_interaction
  4006: "Something went wrong. Please try again.", // invalid_intent_config
  4007: "Configuration error. Please contact support.", // api_secret_mismatch
  4008: "Authentication failed. Please sign in again.", // invalid_auth_token
  4009: "A dashboard is already open.", // dashboard_already_open
  4010: "Something went wrong. Please try again.", // element_not_found
};

export const scErrorMessage = (e: any): string =>
  SC_ERRORS[e?.code] ||
  e?.response?.data?.error ||
  e?.response?.data?.message ||
  e?.message ||
  "Failed to submit application.";
